export type NicheModule = "scheduling" | "restaurant" | "assistant";

export interface Feature {
  icon: string;
  title: string;
  description: string;
}

export interface NicheConfig {
  slug: string;
  route: string;
  module: NicheModule;
  name: string;
  icon: string;
  tagline: string;
  seo: {
    title: string;
    description: string;
  };
  hero: {
    headline: string;
    subheadline: string;
  };
  features: Feature[];
  steps: string[];
  followUp: {
    title: string;
    subtitle: string;
    items: string[];
  };
  ctaPhrase: string;
}

export const niches: NicheConfig[] = [
  // Módulo scheduling
  {
    slug: "saude",
    route: "/saude",
    module: "scheduling",
    name: "Saúde",
    icon: "mdi:hospital-box-outline",
    tagline: "Agendamentos, retornos e follow-up sem esforço",
    seo: {
      title: "Assistente de WhatsApp com IA para Clínicas | Alfred",
      description: "O Alfred agenda consultas, confirma retornos e faz follow-up de pacientes direto no WhatsApp, 24 horas por dia, sem sua equipe precisar responder.",
    },
    hero: {
      headline: `Sua clínica atende no WhatsApp,<br/>mesmo quando você não está. É o <span style="color:#E4DA1B;">Alfred</span>.`,
      subheadline: "O Alfred agenda consultas, confirma retornos e faz follow-up de pacientes — sozinho, 24 horas por dia.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento automático",
        description: "Paciente vê os serviços disponíveis e escolhe dia e horário direto no WhatsApp, sem precisar ligar.",
      },
      {
        icon: "mdi:pencil-outline",
        title: "Remarcação sem atrito",
        description: "O paciente pede para mudar o horário e o Alfred atualiza o agendamento na hora, sem passar por ninguém da equipe.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Cobrança por Pix no chat",
        description: "Consulta particular ou taxa de reserva? O Alfred manda a chave Pix e confirma o pagamento direto na conversa.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para sua equipe",
        description: "Caso complexo ou dúvida clínica? O Alfred identifica e passa a conversa para um atendente humano.",
      },
    ],
    steps: [
      "Cadastre seus serviços, profissionais e horários disponíveis",
      "Paciente agenda, remarca ou cancela direto pelo WhatsApp",
      "Alfred confirma o horário e faz o follow-up do retorno sozinho",
    ],
    followUp: {
      title: "Pacientes voltam mais quando o Alfred lembra deles.",
      subtitle: "O Alfred identifica quem está com retorno perto e manda a mensagem certa, no momento certo.",
      items: [
        "Reduza faltas com lembrete automático antes da consulta",
        "Reative pacientes que não retornam há tempos",
        "Acompanhe quem tem retorno agendado sem precisar de planilha",
      ],
    },
    ctaPhrase: "Sua agenda mais cheia, seus pacientes mais bem cuidados.",
  },
  {
    slug: "barbearia",
    route: "/barbearia",
    module: "scheduling",
    name: "Barbearia",
    icon: "mdi:content-cut",
    tagline: "Horários preenchidos, clientes bem atendidos",
    seo: {
      title: "Assistente de WhatsApp com IA para Barbearia | Alfred",
      description: "O Alfred agenda cortes e barba, apresenta preços e lembra o cliente do horário — tudo pelo WhatsApp, mesmo fora do expediente.",
    },
    hero: {
      headline: `Sua barbearia sempre ocupada.<br/>O <span style="color:#E4DA1B;">Alfred</span> preenche a agenda 24h.`,
      subheadline: "Do agendamento ao lembrete, o Alfred cuida do seu WhatsApp enquanto você cuida dos clientes.",
    },
    features: [
      {
        icon: "mdi:clock-outline",
        title: "Agendamento 24h",
        description: "Cliente marca mesmo fora do horário comercial, sem você precisar responder.",
      },
      {
        icon: "mdi:content-cut",
        title: "Cardápio de serviços",
        description: "Corte, barba, pacotes e preços apresentados direto no chat, sempre atualizados.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix no fechamento",
        description: "Sinal ou pagamento antecipado? O Alfred manda a chave Pix na hora de confirmar.",
      },
      {
        icon: "mdi:heart-outline",
        title: "Follow-up de fidelização",
        description: "Reengaja clientes que sumiram com mensagens automáticas geradas por IA.",
      },
    ],
    steps: [
      "Cadastre seus serviços, profissionais e horários disponíveis",
      "Cliente agenda pelo WhatsApp sem precisar ligar",
      "Alfred confirma e mantém o cliente fiel com follow-up automático",
    ],
    followUp: {
      title: "Clientes voltam mais quando alguém lembra deles. O Alfred lembra.",
      subtitle: "O Alfred identifica quem está sumido e manda a mensagem certa, no momento certo.",
      items: [
        "Reative clientes que não marcam faz semanas",
        "Envie promoções para sua base de clientes fiéis",
        "Acompanhe quem agendou e nunca apareceu",
      ],
    },
    ctaPhrase: "Cadeira ocupada, negócio crescendo.",
  },
  {
    slug: "estetica",
    route: "/estetica",
    module: "scheduling",
    name: "Estética/Salão de Beleza",
    icon: "mdi:spa-outline",
    tagline: "Agenda cheia sem ninguém no telefone",
    seo: {
      title: "Assistente de WhatsApp com IA para Salão de Beleza | Alfred",
      description: "O Alfred agenda manicure, cabelo e procedimentos estéticos pelo WhatsApp, cobra sinal por Pix e reengaja clientes — sem sua recepção precisar responder.",
    },
    hero: {
      headline: `Seu salão agenda sozinho.<br/>O <span style="color:#E4DA1B;">Alfred</span> cuida do WhatsApp.`,
      subheadline: "Cabelo, unha, procedimento — o cliente escolhe o serviço e o horário direto na conversa.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agenda de serviços por profissional",
        description: "Cliente escolhe o procedimento e vê os horários realmente disponíveis, sem ida e volta de mensagem.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Sinal via Pix",
        description: "Reduza furos de agenda cobrando um sinal por Pix direto na confirmação do horário.",
      },
      {
        icon: "mdi:pencil-outline",
        title: "Remarcação fácil",
        description: "Cliente pede para trocar o horário e o Alfred atualiza sozinho, sem sua equipe precisar mexer na agenda.",
      },
      {
        icon: "mdi:heart-outline",
        title: "Reengajamento automático",
        description: "Alfred identifica clientes que sumiram e manda uma mensagem convidando a voltar.",
      },
    ],
    steps: [
      "Cadastre seus serviços, profissionais e horários",
      "Cliente escolhe o serviço e agenda pelo WhatsApp",
      "Alfred confirma, cobra sinal e reengaja quem sumiu",
    ],
    followUp: {
      title: "Cliente esquece a agenda. O Alfred não deixa.",
      subtitle: "Reduza furos e reative clientes antigos com mensagens automáticas no momento certo.",
      items: [
        "Lembrete automático perto do horário marcado",
        "Reative clientes que não voltam há meses",
        "Divulgue promoções para quem já é cliente",
      ],
    },
    ctaPhrase: "Agenda cheia, salão sempre em movimento.",
  },
  {
    slug: "academia",
    route: "/academia",
    module: "scheduling",
    name: "Academia/Personal Trainer",
    icon: "mdi:dumbbell",
    tagline: "Aulas e avaliações agendadas sem esforço",
    seo: {
      title: "Assistente de WhatsApp com IA para Academias e Personal | Alfred",
      description: "O Alfred agenda aulas, avaliação física e horários de personal pelo WhatsApp, e reengaja alunos que sumiram — tudo automático.",
    },
    hero: {
      headline: `Sua academia treina até no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> agenda por você.`,
      subheadline: "Aula experimental, avaliação física, horário de personal — o Alfred organiza tudo direto na conversa.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento de aulas e avaliações",
        description: "Aluno vê os horários disponíveis de cada modalidade e agenda sem precisar ligar.",
      },
      {
        icon: "mdi:account-group-outline",
        title: "Turmas com vaga limitada",
        description: "Pilates, yoga ou aula em grupo: o Alfred só oferece os horários que ainda têm vaga.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Cobrança de matrícula por Pix",
        description: "Fechou o plano? O Alfred envia a chave Pix e confirma o pagamento na hora.",
      },
      {
        icon: "mdi:heart-outline",
        title: "Reengajamento de alunos",
        description: "Alfred identifica quem parou de treinar e manda uma mensagem convidando a voltar.",
      },
    ],
    steps: [
      "Cadastre suas modalidades, turmas e horários disponíveis",
      "Aluno agenda aula, avaliação ou horário de personal pelo WhatsApp",
      "Alfred confirma e reengaja quem some da academia",
    ],
    followUp: {
      title: "Aluno sumiu do treino? O Alfred vai atrás.",
      subtitle: "Reative matrículas paradas e mantenha a academia cheia com mensagens automáticas.",
      items: [
        "Identifique alunos que pararam de frequentar",
        "Envie promoções de renovação de plano",
        "Lembrete automático antes da aula marcada",
      ],
    },
    ctaPhrase: "Aula marcada, aluno frequente, academia cheia.",
  },
  {
    slug: "veterinaria",
    route: "/veterinaria",
    module: "scheduling",
    name: "Clínica Veterinária",
    icon: "mdi:paw-outline",
    tagline: "Consultas e vacinas do pet, sem telefone",
    seo: {
      title: "Assistente de WhatsApp com IA para Clínica Veterinária | Alfred",
      description: "O Alfred agenda consultas, vacinas e retornos veterinários pelo WhatsApp, e lembra o tutor da próxima dose — automaticamente.",
    },
    hero: {
      headline: `A consulta do pet, marcada no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> cuida da agenda.`,
      subheadline: "Consulta, vacina ou retorno — o tutor agenda sem ligar e o Alfred lembra na hora certa.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento de consultas e vacinas",
        description: "Tutor escolhe o serviço, o dia e o horário direto no WhatsApp.",
      },
      {
        icon: "mdi:bell-outline",
        title: "Lembrete de retorno e vacina",
        description: "Alfred avisa o tutor quando a próxima dose ou retorno está chegando.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix na confirmação",
        description: "Consulta particular? O Alfred envia a chave Pix e confirma o agendamento.",
      },
      {
        icon: "mdi:account-voice",
        title: "Encaminhamento de urgência",
        description: "Caso de urgência? O Alfred identifica e transfere direto para sua equipe.",
      },
    ],
    steps: [
      "Cadastre seus serviços veterinários e horários disponíveis",
      "Tutor agenda consulta, vacina ou retorno pelo WhatsApp",
      "Alfred confirma e lembra o tutor na data certa",
    ],
    followUp: {
      title: "O pet não esquece a vacina. O Alfred também não.",
      subtitle: "O Alfred acompanha o calendário de retorno e vacina de cada pet e avisa o tutor sozinho.",
      items: [
        "Lembrete automático de vacina e retorno",
        "Reative tutores que não voltam há tempos",
        "Acompanhe consultas marcadas sem planilha",
      ],
    },
    ctaPhrase: "Mais consultas marcadas, pets sempre em dia.",
  },
  {
    slug: "odontologia",
    route: "/odontologia",
    module: "scheduling",
    name: "Odontologia",
    icon: "mdi:tooth-outline",
    tagline: "Consultas e retornos sem no-show",
    seo: {
      title: "Assistente de WhatsApp com IA para Odontologia | Alfred",
      description: "O Alfred agenda consultas odontológicas, confirma retornos e reduz faltas com lembrete automático — direto pelo WhatsApp.",
    },
    hero: {
      headline: `Sua clínica odontológica agenda sozinha.<br/>É o <span style="color:#E4DA1B;">Alfred</span> no WhatsApp.`,
      subheadline: "O Alfred marca consultas, confirma retornos e reduz faltas — sem sua recepção parar para responder.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento por procedimento",
        description: "Paciente escolhe o tipo de consulta e vê os horários realmente disponíveis.",
      },
      {
        icon: "mdi:bell-outline",
        title: "Confirmação e lembrete",
        description: "Alfred confirma a consulta e lembra o paciente antes do dia, reduzindo faltas.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix para sinal ou consulta",
        description: "Cobrança de sinal ou consulta particular direto no chat, sem terceiros.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para a equipe",
        description: "Dúvida clínica ou caso específico? O Alfred passa para um atendente humano.",
      },
    ],
    steps: [
      "Cadastre procedimentos, dentistas e horários disponíveis",
      "Paciente agenda ou remarca consulta pelo WhatsApp",
      "Alfred confirma, lembra e reduz as faltas na agenda",
    ],
    followUp: {
      title: "Menos falta, mais consulta. O Alfred confirma por você.",
      subtitle: "O Alfred lembra o paciente antes do horário marcado e reativa quem não retorna.",
      items: [
        "Lembrete automático antes da consulta",
        "Reative pacientes com retorno atrasado",
        "Reduza o no-show sem ligação manual",
      ],
    },
    ctaPhrase: "Agenda cheia, cadeira sempre ocupada.",
  },
  {
    slug: "oficina-mecanica",
    route: "/oficina-mecanica",
    module: "scheduling",
    name: "Oficina Mecânica",
    icon: "mdi:car-wrench",
    tagline: "Revisão e troca de óleo agendadas no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Oficina Mecânica | Alfred",
      description: "O Alfred agenda revisão, troca de óleo e serviços da oficina pelo WhatsApp e avisa o cliente quando o carro está pronto.",
    },
    hero: {
      headline: `Sua oficina agenda pelo WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> organiza tudo.`,
      subheadline: "Cliente marca revisão, troca de óleo ou serviço direto na conversa, sem ligar pra saber horário.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento de serviços",
        description: "Cliente vê os serviços disponíveis e escolhe o dia e horário para levar o carro.",
      },
      {
        icon: "mdi:car-cog",
        title: "Cardápio de serviços claro",
        description: "Revisão, troca de óleo, alinhamento — tudo com preço apresentado no chat.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix no fechamento",
        description: "Serviço concluído? O Alfred envia a chave Pix e confirma o pagamento.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para o mecânico",
        description: "Orçamento fora do padrão ou dúvida técnica? O Alfred passa para sua equipe.",
      },
    ],
    steps: [
      "Cadastre seus serviços, box e horários disponíveis",
      "Cliente agenda o serviço pelo WhatsApp",
      "Alfred confirma o horário e cobra quando o carro fica pronto",
    ],
    followUp: {
      title: "Cliente esquece a revisão. O Alfred lembra.",
      subtitle: "Reative clientes que não fazem revisão há tempos com mensagens automáticas.",
      items: [
        "Lembrete de revisão periódica",
        "Reative clientes que sumiram da oficina",
        "Acompanhe agendamentos sem planilha",
      ],
    },
    ctaPhrase: "Box ocupado, cliente satisfeito, oficina rodando.",
  },
  {
    slug: "quadra-esportiva",
    route: "/quadra-esportiva",
    module: "scheduling",
    name: "Quadra/Arena Esportiva",
    icon: "mdi:soccer-field",
    tagline: "Reserva de quadra sem ligação",
    seo: {
      title: "Assistente de WhatsApp com IA para Quadras e Arenas | Alfred",
      description: "O Alfred reserva quadras e horários de jogo pelo WhatsApp, cobra o sinal por Pix e confirma a reserva na hora — sem atendente.",
    },
    hero: {
      headline: `Sua quadra reservada no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> confirma sozinho.`,
      subheadline: "Cliente escolhe a quadra, o dia e o horário — e o Alfred confirma a reserva na hora.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Reserva de quadra por horário",
        description: "Cliente vê as quadras e horários realmente disponíveis e reserva direto no chat.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Sinal via Pix",
        description: "Reduza reserva furada cobrando sinal por Pix na confirmação do horário.",
      },
      {
        icon: "mdi:pencil-outline",
        title: "Remarcação sem atrito",
        description: "Cliente pede para trocar o horário e o Alfred atualiza a reserva sozinho.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para sua equipe",
        description: "Evento especial ou pacote fechado? O Alfred passa para um atendente.",
      },
    ],
    steps: [
      "Cadastre suas quadras, modalidades e horários disponíveis",
      "Cliente escolhe e reserva o horário pelo WhatsApp",
      "Alfred confirma, cobra o sinal e evita reserva furada",
    ],
    followUp: {
      title: "Horário vago é receita perdida. O Alfred não deixa vago.",
      subtitle: "Reative clientes que não jogam há tempos e preencha os horários fracos.",
      items: [
        "Lembrete automático antes do horário reservado",
        "Reative grupos que não voltam a jogar",
        "Divulgue horários livres da semana",
      ],
    },
    ctaPhrase: "Quadra cheia, agenda sem furo.",
  },
  {
    slug: "coworking",
    route: "/coworking",
    module: "scheduling",
    name: "Coworking",
    icon: "mdi:office-building-outline",
    tagline: "Salas e estações reservadas no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Coworking | Alfred",
      description: "O Alfred reserva salas de reunião e estações de trabalho pelo WhatsApp, cobra por Pix e confirma na hora — sem recepção.",
    },
    hero: {
      headline: `Sua sala reservada no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> confirma na hora.`,
      subheadline: "Sala de reunião, estação fixa ou dia avulso — o cliente reserva direto na conversa.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Reserva de salas e estações",
        description: "Cliente vê a disponibilidade real de cada espaço e reserva o horário sozinho.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Cobrança por Pix",
        description: "Diária, sala avulsa ou plano mensal — o Alfred cobra direto no chat.",
      },
      {
        icon: "mdi:pencil-outline",
        title: "Remarcação sem atrito",
        description: "Cliente pede para trocar horário ou sala e o Alfred atualiza a reserva.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para sua equipe",
        description: "Plano corporativo ou proposta especial? O Alfred passa para um atendente.",
      },
    ],
    steps: [
      "Cadastre suas salas, estações e horários disponíveis",
      "Cliente reserva o espaço pelo WhatsApp",
      "Alfred confirma, cobra e mantém a agenda organizada",
    ],
    followUp: {
      title: "Espaço vago é custo parado. O Alfred preenche a agenda.",
      subtitle: "Reative clientes antigos e divulgue horários livres com mensagens automáticas.",
      items: [
        "Lembrete antes do horário reservado",
        "Reative clientes que não voltam a reservar",
        "Divulgue vagas de plano mensal",
      ],
    },
    ctaPhrase: "Espaço ocupado, coworking rodando.",
  },
  {
    slug: "clinica-terapia",
    route: "/clinica-terapia",
    module: "scheduling",
    name: "Clínica de Terapia",
    icon: "mdi:hand-heart-outline",
    tagline: "Sessões agendadas com sigilo e sem esforço",
    seo: {
      title: "Assistente de WhatsApp com IA para Fisioterapia e Psicologia | Alfred",
      description: "O Alfred agenda sessões de fisioterapia e psicologia pelo WhatsApp, confirma retornos e reduz faltas — com total discrição.",
    },
    hero: {
      headline: `Sua agenda de sessões, no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> confirma por você.`,
      subheadline: "Fisioterapia, psicologia ou terapia — o paciente agenda a sessão sem precisar ligar.",
    },
    features: [
      {
        icon: "mdi:calendar-check-outline",
        title: "Agendamento de sessões",
        description: "Paciente escolhe o profissional, o dia e o horário direto no WhatsApp.",
      },
      {
        icon: "mdi:bell-outline",
        title: "Confirmação e lembrete",
        description: "Alfred confirma a sessão e lembra o paciente antes do horário, reduzindo faltas.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix na confirmação",
        description: "Cobrança da sessão direto no chat, sem passar dado de cartão por terceiros.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para o profissional",
        description: "Situação que exige atenção direta? O Alfred passa a conversa para sua equipe.",
      },
    ],
    steps: [
      "Cadastre profissionais, tipos de sessão e horários disponíveis",
      "Paciente agenda ou remarca a sessão pelo WhatsApp",
      "Alfred confirma, lembra e reduz as faltas na agenda",
    ],
    followUp: {
      title: "Paciente esquece a sessão. O Alfred lembra por ele.",
      subtitle: "Reduza faltas e mantenha o vínculo terapêutico com lembretes automáticos.",
      items: [
        "Lembrete automático antes da sessão",
        "Reative pacientes que pausaram o acompanhamento",
        "Acompanhe a agenda sem depender de planilha",
      ],
    },
    ctaPhrase: "Sessão marcada, paciente cuidado, agenda em dia.",
  },
  // Módulo restaurant
  {
    slug: "restaurante",
    route: "/restaurante",
    module: "restaurant",
    name: "Restaurante",
    icon: "mdi:silverware-fork-knife",
    tagline: "Pedidos direto no WhatsApp, sem comissão",
    seo: {
      title: "Assistente de WhatsApp com IA para Restaurantes | Alfred",
      description: "O Alfred apresenta o cardápio, recebe pedidos para entrega, retirada ou mesa e cobra por Pix — direto no WhatsApp, sem comissão de app.",
    },
    hero: {
      headline: `Pedidos no WhatsApp,<br/>sem app de terceiro, sem comissão. É o <span style="color:#E4DA1B;">Alfred</span>.`,
      subheadline: "O Alfred apresenta seu cardápio, recebe o pedido e confirma tudo automaticamente.",
    },
    features: [
      {
        icon: "mdi:silverware-fork-knife",
        title: "Cardápio interativo",
        description: "Cliente navega por categorias e escolhe os pratos direto na conversa.",
      },
      {
        icon: "mdi:clipboard-check-outline",
        title: "Pedido para mesa, retirada ou entrega",
        description: "Alfred pergunta o tipo de pedido e pede endereço ou número da mesa quando necessário.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pagamento via Pix",
        description: "Alfred envia a chave Pix automaticamente e confirma o pedido após o pagamento.",
      },
      {
        icon: "mdi:progress-clock",
        title: "Acompanhamento de pedido",
        description: "Cliente pergunta 'cadê meu pedido' e o Alfred responde com o status na hora.",
      },
    ],
    steps: [
      "Cadastre seu cardápio, categorias e formas de entrega",
      "Cliente monta o pedido e escolhe retirada, entrega ou mesa",
      "Alfred confirma o pedido e você só se preocupa em servir",
    ],
    followUp: {
      title: "Cliente esquece de pedir de novo. O Alfred não deixa esquecer.",
      subtitle: "Reative clientes antigos e avise sobre promoções com mensagens automáticas.",
      items: [
        "Reative clientes que não pedem há tempos",
        "Divulgue promoções e pratos novos",
        "Acompanhe pedidos recorrentes sem esforço",
      ],
    },
    ctaPhrase: "Mais pedidos, zero comissão para aplicativo.",
  },
  {
    slug: "lanchonete",
    route: "/lanchonete",
    module: "restaurant",
    name: "Lanchonete",
    icon: "mdi:food-outline",
    tagline: "Pedido sem fila, direto no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Lanchonete | Alfred",
      description: "O Alfred recebe pedidos de lanche, combo e bebida pelo WhatsApp, cobra por Pix e confirma tudo sem fila e sem ligação.",
    },
    hero: {
      headline: `Seu balcão no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> atende sem fila.`,
      subheadline: "O Alfred recebe pedidos, organiza tudo e confirma sem você precisar parar o balcão.",
    },
    features: [
      {
        icon: "mdi:food-outline",
        title: "Pedidos rápidos",
        description: "Cliente pede sem ligar e sem esperar em fila de atendimento.",
      },
      {
        icon: "mdi:tag-multiple-outline",
        title: "Combos e promoções",
        description: "Alfred apresenta combos e promoções do dia automaticamente no chat.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix antes do preparo",
        description: "Pagamento confirmado antes de começar o pedido, sem calote no final.",
      },
      {
        icon: "mdi:clipboard-check-outline",
        title: "Confirmação clara do pedido",
        description: "Cliente recebe a lista exata do que pediu, com preço e forma de entrega.",
      },
    ],
    steps: [
      "Cadastre seu cardápio, combos e formas de entrega",
      "Cliente pede pelo WhatsApp a qualquer hora",
      "Alfred confirma, cobra e você só prepara",
    ],
    followUp: {
      title: "Fila zero também no reengajamento. O Alfred traz o cliente de volta.",
      subtitle: "Reative quem não pede há tempos e divulgue promoções automaticamente.",
      items: [
        "Reative clientes que sumiram",
        "Avise sobre combo e promoção do dia",
        "Acompanhe pedidos recorrentes",
      ],
    },
    ctaPhrase: "Fila zero, pedido certo, cliente satisfeito.",
  },
  {
    slug: "hamburgueria",
    route: "/hamburgueria",
    module: "restaurant",
    name: "Hamburgueria",
    icon: "ic:baseline-lunch-dining",
    tagline: "Do pedido ao delivery, tudo automatizado",
    seo: {
      title: "Assistente de WhatsApp com IA para Hamburgueria | Alfred",
      description: "O Alfred recebe pedidos de hambúrguer com toda a customização, cobra por Pix antes do preparo e confirma entrega ou retirada.",
    },
    hero: {
      headline: `Hambúrguer artesanal, pedido automatizado.<br/>Chegou o <span style="color:#E4DA1B;">Alfred</span>.`,
      subheadline: "O Alfred recebe pedidos com todas as customizações — ponto, adicionais, retirada ou delivery.",
    },
    features: [
      {
        icon: "mdi:tune-variant",
        title: "Customização completa",
        description: "Ponto da carne, ingredientes extras e remoções, tudo registrado sem confusão.",
      },
      {
        icon: "mdi:moped-outline",
        title: "Delivery ou retirada",
        description: "Alfred confirma a opção escolhida e coleta o endereço automaticamente quando é entrega.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix antes de acender a chapa",
        description: "Pagamento confirmado antes do preparo. Zero calote.",
      },
      {
        icon: "mdi:progress-clock",
        title: "Acompanhamento do pedido",
        description: "Cliente pergunta o status e o Alfred responde na hora, sem precisar chamar ninguém.",
      },
    ],
    steps: [
      "Cadastre seu cardápio com todas as opções de customização",
      "Cliente monta o pedido completo pelo WhatsApp",
      "Alfred confirma, cobra e encaminha para a cozinha",
    ],
    followUp: {
      title: "Menos mensagem pra responder. O Alfred cuida do resto.",
      subtitle: "Reative clientes que sumiram e avise sobre lançamentos com mensagens automáticas.",
      items: [
        "Reative clientes que não pedem há tempos",
        "Divulgue novo sabor ou combo",
        "Acompanhe pedidos recorrentes",
      ],
    },
    ctaPhrase: "Menos tempo respondendo mensagem, mais tempo na chapa.",
  },
  {
    slug: "pizzaria",
    route: "/pizzaria",
    module: "restaurant",
    name: "Pizzaria",
    icon: "ic:baseline-local-pizza",
    tagline: "Sabores escolhidos, entrega confirmada",
    seo: {
      title: "Assistente de WhatsApp com IA para Pizzaria | Alfred",
      description: "O Alfred recebe o pedido completo de pizza — sabor, tamanho, borda e endereço — e cobra por Pix antes de assar.",
    },
    hero: {
      headline: `Peça sua pizza pelo WhatsApp.<br/>Sem app, sem taxa. É o <span style="color:#E4DA1B;">Alfred</span>.`,
      subheadline: "O Alfred recebe o pedido completo — sabor, tamanho, endereço — e confirma em segundos.",
    },
    features: [
      {
        icon: "mdi:food-variant",
        title: "Montagem de pizza no chat",
        description: "Sabores, tamanhos e bordas escolhidos direto na conversa, sem confusão.",
      },
      {
        icon: "mdi:moped-outline",
        title: "Delivery confirmado",
        description: "Alfred pede o endereço completo e confirma a entrega automaticamente.",
      },
      {
        icon: "mdi:tag-multiple-outline",
        title: "Promoções automáticas",
        description: "Quinta do 2x1? O Alfred já sabe e apresenta a promoção certa na hora.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pix antes de assar",
        description: "Cobra antes do forno, evita cancelamento de última hora.",
      },
    ],
    steps: [
      "Cadastre sabores, tamanhos e área de entrega",
      "Cliente monta o pedido completo pelo WhatsApp",
      "Alfred confirma, cobra e aciona sua cozinha",
    ],
    followUp: {
      title: "Cliente esquece de pedir pizza. O Alfred lembra na sexta certa.",
      subtitle: "Reative clientes antigos e divulgue promoções com mensagens automáticas.",
      items: [
        "Reative clientes que não pedem há tempos",
        "Divulgue a promoção do dia certo",
        "Acompanhe pedidos recorrentes",
      ],
    },
    ctaPhrase: "Mais pizzas entregues, menos ligação perdida.",
  },
  {
    slug: "petshop",
    route: "/petshop",
    module: "restaurant",
    name: "Petshop",
    icon: "mdi:paw",
    tagline: "Ração e produtos do pet, sem sair de casa",
    seo: {
      title: "Assistente de WhatsApp com IA para Petshop | Alfred",
      description: "O Alfred recebe pedidos de ração, produtos e acessórios pelo WhatsApp, cobra por Pix e confirma a entrega — sem app.",
    },
    hero: {
      headline: `Ração e produtos do pet, no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> entrega.`,
      subheadline: "O Alfred apresenta o catálogo do petshop, recebe o pedido e confirma a entrega sozinho.",
    },
    features: [
      {
        icon: "mdi:paw",
        title: "Catálogo de produtos",
        description: "Ração, petiscos e acessórios apresentados por categoria direto no chat.",
      },
      {
        icon: "mdi:moped-outline",
        title: "Delivery confirmado",
        description: "Alfred coleta o endereço e confirma a entrega automaticamente.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pagamento via Pix",
        description: "Pedido confirmado só depois do pagamento, sem risco de calote.",
      },
      {
        icon: "mdi:progress-clock",
        title: "Acompanhamento de pedido",
        description: "Cliente pergunta o status do pedido e o Alfred responde na hora.",
      },
    ],
    steps: [
      "Cadastre seu catálogo de produtos e área de entrega",
      "Cliente monta o pedido pelo WhatsApp",
      "Alfred confirma, cobra e você só separa o pedido",
    ],
    followUp: {
      title: "Ração acaba, o Alfred lembra antes.",
      subtitle: "Reative clientes recorrentes e avise sobre promoções com mensagens automáticas.",
      items: [
        "Lembrete de recompra quando a ração costuma acabar",
        "Reative clientes que não pedem há tempos",
        "Divulgue promoções de produtos",
      ],
    },
    ctaPhrase: "Pet com estoque em dia, sem sair de casa.",
  },
  {
    slug: "farmacia",
    route: "/farmacia",
    module: "restaurant",
    name: "Farmácia",
    icon: "mdi:pill",
    tagline: "Pedido de medicamento, entrega confirmada",
    seo: {
      title: "Assistente de WhatsApp com IA para Farmácias | Alfred",
      description: "O Alfred recebe pedidos de medicamentos e produtos pelo WhatsApp, confirma o endereço de entrega e cobra por Pix.",
    },
    hero: {
      headline: `Sua farmácia atende pelo WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> confirma o pedido.`,
      subheadline: "O Alfred apresenta o catálogo de produtos, recebe o pedido e confirma a entrega automaticamente.",
    },
    features: [
      {
        icon: "mdi:pill",
        title: "Catálogo de produtos",
        description: "Medicamentos isentos de prescrição e produtos de conveniência, com preço no chat.",
      },
      {
        icon: "mdi:moped-outline",
        title: "Entrega confirmada",
        description: "Alfred coleta o endereço completo e confirma a entrega automaticamente.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pagamento via Pix",
        description: "Pedido confirmado só depois do pagamento, sem risco de calote.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para o farmacêutico",
        description: "Dúvida sobre um produto ou pedido especial? O Alfred passa para sua equipe.",
      },
    ],
    steps: [
      "Cadastre seu catálogo de produtos e área de entrega",
      "Cliente monta o pedido pelo WhatsApp",
      "Alfred confirma, cobra e você despacha a entrega",
    ],
    followUp: {
      title: "Cliente esquece de repor o remédio de uso contínuo. O Alfred lembra.",
      subtitle: "Reative clientes recorrentes e avise sobre promoções com mensagens automáticas.",
      items: [
        "Lembrete de recompra de item recorrente",
        "Reative clientes que não pedem há tempos",
        "Divulgue promoções de produtos",
      ],
    },
    ctaPhrase: "Pedido rápido, entrega confirmada, cliente tranquilo.",
  },
  {
    slug: "adega",
    route: "/adega",
    module: "restaurant",
    name: "Adega/Distribuidora de Bebidas",
    icon: "mdi:bottle-wine-outline",
    tagline: "Bebida gelada, pedida no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Adega e Distribuidora | Alfred",
      description: "O Alfred recebe pedidos de bebidas pelo WhatsApp, apresenta o catálogo, cobra por Pix e confirma a entrega — sem app.",
    },
    hero: {
      headline: `Bebida pedida no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> confirma e entrega.`,
      subheadline: "O Alfred apresenta o catálogo de bebidas, recebe o pedido e confirma a entrega automaticamente.",
    },
    features: [
      {
        icon: "mdi:bottle-wine-outline",
        title: "Catálogo de bebidas",
        description: "Cerveja, vinho, destilado — cliente escolhe por categoria direto no chat.",
      },
      {
        icon: "mdi:moped-outline",
        title: "Delivery confirmado",
        description: "Alfred coleta o endereço e confirma a entrega automaticamente.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Pagamento via Pix",
        description: "Pedido confirmado só depois do pagamento, sem risco de calote.",
      },
      {
        icon: "mdi:tag-multiple-outline",
        title: "Promoções automáticas",
        description: "Combo de fim de semana? O Alfred já apresenta a promoção certa.",
      },
    ],
    steps: [
      "Cadastre seu catálogo de bebidas e área de entrega",
      "Cliente monta o pedido pelo WhatsApp",
      "Alfred confirma, cobra e você despacha a entrega",
    ],
    followUp: {
      title: "Fim de semana chegando, o Alfred já avisa a promoção.",
      subtitle: "Reative clientes recorrentes e divulgue promoções automaticamente.",
      items: [
        "Divulgue a promoção de fim de semana",
        "Reative clientes que não pedem há tempos",
        "Acompanhe pedidos recorrentes",
      ],
    },
    ctaPhrase: "Pedido rápido, entrega gelada, cliente satisfeito.",
  },
  // Módulo assistant
  {
    slug: "captacao-de-leads",
    route: "/captacao-de-leads",
    module: "assistant",
    name: "Marketing Digital/Captação de Leads",
    icon: "mdi:account-plus-outline",
    tagline: "Capture e qualifique leads direto no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Captação de Leads | Alfred",
      description: "O Alfred qualifica leads, coleta dados de contato e envia direto pro seu CRM — tudo automático, direto no WhatsApp.",
    },
    hero: {
      headline: `Lead chegou, o <span style="color:#E4DA1B;">Alfred</span> já qualifica.<br/>Sem perder tempo, sem perder lead.`,
      subheadline: "O Alfred conversa com quem chega pelo anúncio, qualifica o interesse e manda o lead pronto pro seu time.",
    },
    features: [
      {
        icon: "mdi:message-text-outline",
        title: "Qualificação automática",
        description: "Alfred conversa com quem chega pelo anúncio ou link e identifica o nível de interesse antes de continuar.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captura de dados do lead",
        description: "Nome, telefone e e-mail coletados durante a própria conversa, sem formulário.",
      },
      {
        icon: "mdi:webhook",
        title: "Integração com seu CRM",
        description: "Alfred envia o lead qualificado direto pro seu sistema ou planilha assim que a conversa termina.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência para o time comercial",
        description: "Lead pronto pra fechar? Alfred identifica e passa direto pro vendedor.",
      },
    ],
    steps: [
      "Cadastre as perguntas de qualificação e o destino do lead (CRM, planilha ou webhook)",
      "Lead conversa com o Alfred a partir do anúncio, link ou WhatsApp",
      "Alfred qualifica, coleta os dados e entrega o lead pronto pro seu time",
    ],
    followUp: {
      title: "Lead esfriou? O Alfred esquenta de novo.",
      subtitle: "Reative leads que pararam de responder com mensagens automáticas geradas por IA.",
      items: [
        "Reative leads que sumiram no meio da conversa",
        "Nutra a base com conteúdo e novidades automaticamente",
        "Acompanhe quantos leads viraram conversa real",
      ],
    },
    ctaPhrase: "Mais leads qualificados, menos tempo perdido.",
  },
  {
    slug: "suporte-ao-cliente",
    route: "/suporte-ao-cliente",
    module: "assistant",
    name: "Suporte ao Cliente",
    icon: "mdi:headset",
    tagline: "Dúvidas respondidas 24h, sem fila de espera",
    seo: {
      title: "Assistente de WhatsApp com IA para Suporte ao Cliente | Alfred",
      description: "O Alfred responde dúvidas frequentes, resolve o simples e transfere o complexo para sua equipe — 24 horas por dia, no WhatsApp.",
    },
    hero: {
      headline: `Seu suporte nunca fecha.<br/>O <span style="color:#E4DA1B;">Alfred</span> responde 24h.`,
      subheadline: "Alfred resolve as dúvidas mais comuns sozinho e só chama sua equipe quando realmente precisa.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas da sua base de conhecimento",
        description: "Alfred responde dúvidas frequentes com as informações reais do seu negócio, sem inventar.",
      },
      {
        icon: "mdi:format-list-bulleted",
        title: "Menu de opções guiado",
        description: "Cliente escolhe o assunto por botão e chega direto na resposta certa, sem digitar tudo.",
      },
      {
        icon: "mdi:account-voice",
        title: "Escalonamento para equipe humana",
        description: "Caso complexo? Alfred identifica e transfere a conversa com o histórico completo.",
      },
      {
        icon: "mdi:clock-fast",
        title: "Atendimento fora do horário",
        description: "Noite, fim de semana ou feriado: Alfred continua respondendo enquanto sua equipe descansa.",
      },
    ],
    steps: [
      "Cadastre as perguntas frequentes e políticas do seu negócio na base de conhecimento",
      "Cliente manda a dúvida pelo WhatsApp a qualquer hora",
      "Alfred responde sozinho ou transfere pra sua equipe quando necessário",
    ],
    followUp: {
      title: "Cliente insatisfeito não some. O Alfred acompanha o caso.",
      subtitle: "Alfred identifica quem ficou sem resposta e retoma o contato automaticamente.",
      items: [
        "Retome conversas que ficaram sem resposta",
        "Avise sobre atualizações e resolução de chamados",
        "Acompanhe a satisfação sem depender de planilha",
      ],
    },
    ctaPhrase: "Suporte rápido, cliente satisfeito, equipe aliviada.",
  },
  {
    slug: "pre-atendimento",
    route: "/pre-atendimento",
    module: "assistant",
    name: "Pré-atendimento",
    icon: "mdi:forum-outline",
    tagline: "A primeira resposta em segundos, pra qualquer negócio",
    seo: {
      title: "Assistente de WhatsApp com IA para Pré-atendimento | Alfred",
      description: "O Alfred recebe o primeiro contato, entende o que o cliente precisa e direciona certo — pra qualquer tipo de negócio, direto no WhatsApp.",
    },
    hero: {
      headline: `A primeira resposta, sempre na hora.<br/>É o <span style="color:#E4DA1B;">Alfred</span> no seu WhatsApp.`,
      subheadline: "Alfred recebe todo mundo que chega no seu WhatsApp, entende o que precisa e direciona pro lugar certo.",
    },
    features: [
      {
        icon: "mdi:forum-outline",
        title: "Triagem automática",
        description: "Alfred identifica o motivo do contato e direciona pro setor ou resposta certa.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Coleta de dados iniciais",
        description: "Nome, telefone e o que o cliente precisa, registrados antes de qualquer atendimento humano.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Respostas institucionais",
        description: "Endereço, horário de funcionamento, formas de pagamento — Alfred responde sozinho.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência pro time certo",
        description: "Quando o assunto exige uma pessoa, Alfred passa a conversa já com o contexto completo.",
      },
    ],
    steps: [
      "Cadastre as informações institucionais e os setores/pessoas de destino",
      "Cliente manda a primeira mensagem pelo WhatsApp",
      "Alfred entende, responde o simples e direciona o resto pro time certo",
    ],
    followUp: {
      title: "Ninguém fica sem resposta. O Alfred garante isso.",
      subtitle: "Alfred acompanha quem entrou em contato e não teve retorno, e retoma sozinho.",
      items: [
        "Retome contatos que ficaram sem resposta",
        "Avise sobre novidades pra quem já falou com você",
        "Acompanhe o volume de atendimento sem esforço",
      ],
    },
    ctaPhrase: "Ninguém espera, todo mundo é bem recebido.",
  },
  {
    slug: "afiliados",
    route: "/afiliados",
    module: "assistant",
    name: "Afiliados",
    icon: "mdi:cash-multiple",
    tagline: "Venda como afiliado sem responder tudo manualmente",
    seo: {
      title: "Assistente de WhatsApp com IA para Afiliados | Alfred",
      description: "O Alfred responde dúvidas sobre o produto que você promove, coleta o interesse do comprador e cobra por Pix — direto no WhatsApp.",
    },
    hero: {
      headline: `Venda como afiliado, sem virar plantão.<br/>O <span style="color:#E4DA1B;">Alfred</span> atende por você.`,
      subheadline: "Alfred responde dúvidas sobre o produto, identifica quem tá pronto pra comprar e cobra direto no chat.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas sobre o produto",
        description: "Alfred conhece as informações do que você promove e responde as dúvidas mais comuns sozinho.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captura de interesse",
        description: "Alfred identifica quem tá interessado e registra o contato pra você acompanhar.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Cobrança direto no chat",
        description: "Comprador decidido? Alfred manda a chave Pix e confirma o pagamento na hora.",
      },
      {
        icon: "mdi:bullhorn-outline",
        title: "Campanha pra sua lista",
        description: "Dispare promoções e lançamentos pra quem já demonstrou interesse.",
      },
    ],
    steps: [
      "Cadastre as informações do produto e a chave Pix de recebimento",
      "Comprador manda mensagem tirando dúvida ou pedindo pra comprar",
      "Alfred responde, cobra e confirma a venda sozinho",
    ],
    followUp: {
      title: "Quem não comprou na hora, o Alfred não esquece.",
      subtitle: "Reative quem demonstrou interesse e não fechou, com mensagens automáticas.",
      items: [
        "Reative quem parou no meio da conversa",
        "Avise sobre promoções e novos lançamentos",
        "Acompanhe quantos interessados viraram venda",
      ],
    },
    ctaPhrase: "Menos tempo respondendo, mais tempo vendendo.",
  },
  {
    slug: "imobiliaria",
    route: "/imobiliaria",
    module: "assistant",
    name: "Imobiliária",
    icon: "mdi:home-city-outline",
    tagline: "Interessado qualificado antes de chegar no corretor",
    seo: {
      title: "Assistente de WhatsApp com IA para Imobiliária | Alfred",
      description: "O Alfred qualifica quem quer comprar ou alugar, tira dúvidas sobre o imóvel e encaminha o interessado pronto pro corretor — direto no WhatsApp.",
    },
    hero: {
      headline: `Interessado no imóvel? <br/>O <span style="color:#E4DA1B;">Alfred</span> já qualifica antes do corretor.`,
      subheadline: "Alfred entende o que o cliente procura, responde as dúvidas iniciais e só chama o corretor quando o interesse é real.",
    },
    features: [
      {
        icon: "mdi:forum-outline",
        title: "Qualificação de interessado",
        description: "Alfred entende se é compra ou aluguel, região e faixa de valor antes de passar o contato.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Respostas sobre o imóvel",
        description: "Condições, documentação e informações do anúncio respondidas na hora, sem esperar o corretor.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captura de contato",
        description: "Nome e telefone registrados durante a conversa, sem formulário.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência pro corretor",
        description: "Interessado de verdade? Alfred passa a conversa já com o contexto completo.",
      },
    ],
    steps: [
      "Cadastre as informações dos imóveis e a equipe de corretores",
      "Interessado manda mensagem a partir do anúncio ou site",
      "Alfred qualifica, responde o inicial e encaminha pro corretor certo",
    ],
    followUp: {
      title: "Interessado sumiu? O Alfred retoma o contato.",
      subtitle: "Reative quem demonstrou interesse e não avançou, com mensagens automáticas.",
      items: [
        "Reative interessados que pararam de responder",
        "Avise sobre novos imóveis que combinam com o que procuram",
        "Acompanhe quantos interessados viraram visita",
      ],
    },
    ctaPhrase: "Mais interessados qualificados, menos tempo perdido.",
  },
  {
    slug: "advocacia",
    route: "/advocacia",
    module: "assistant",
    name: "Escritório de Advocacia",
    icon: "mdi:gavel",
    tagline: "Triagem de caso antes da consulta",
    seo: {
      title: "Assistente de WhatsApp com IA para Advocacia | Alfred",
      description: "O Alfred faz a triagem inicial do caso, responde dúvidas institucionais e encaminha o cliente pro advogado certo — direto no WhatsApp.",
    },
    hero: {
      headline: `Cliente chegou com um caso.<br/>O <span style="color:#E4DA1B;">Alfred</span> já faz a triagem.`,
      subheadline: "Alfred entende a área do direito envolvida, coleta as informações iniciais e encaminha pro advogado responsável.",
    },
    features: [
      {
        icon: "mdi:forum-outline",
        title: "Triagem por área do direito",
        description: "Alfred identifica se é trabalhista, cível, família e direciona pro advogado certo.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Respostas institucionais",
        description: "Horário de atendimento, forma de contratação e documentos necessários, respondidos sozinho.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captura de dados do caso",
        description: "Nome, contato e um resumo do caso registrados antes da consulta.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência pro advogado",
        description: "Caso qualificado? Alfred passa a conversa com o resumo já pronto.",
      },
    ],
    steps: [
      "Cadastre as áreas de atuação e a equipe responsável por cada uma",
      "Cliente descreve o caso pelo WhatsApp",
      "Alfred faz a triagem e encaminha pro advogado certo com o resumo pronto",
    ],
    followUp: {
      title: "Cliente com dúvida não some. O Alfred retoma.",
      subtitle: "Reative quem entrou em contato e não avançou pra consulta.",
      items: [
        "Retome contatos que ficaram sem resposta",
        "Avise sobre novidades do escritório",
        "Acompanhe quantos contatos viraram consulta",
      ],
    },
    ctaPhrase: "Cada caso chega já triado, pronto pra consulta.",
  },
  {
    slug: "contabilidade",
    route: "/contabilidade",
    module: "assistant",
    name: "Escritório de Contabilidade",
    icon: "mdi:calculator-variant-outline",
    tagline: "Dúvida fiscal respondida sem esperar retorno",
    seo: {
      title: "Assistente de WhatsApp com IA para Contabilidade | Alfred",
      description: "O Alfred responde dúvidas fiscais recorrentes, capta novos clientes e dá suporte à carteira ativa — direto no WhatsApp.",
    },
    hero: {
      headline: `Dúvida fiscal? Resposta na hora.<br/>É o <span style="color:#E4DA1B;">Alfred</span> no seu WhatsApp.`,
      subheadline: "Alfred responde as dúvidas mais comuns dos seus clientes e capta quem ainda não é cliente.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas fiscais recorrentes",
        description: "Prazos, documentos e dúvidas comuns respondidas com as informações do seu escritório.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captação de novo cliente",
        description: "Empresa buscando contador? Alfred coleta os dados e encaminha pro seu time comercial.",
      },
      {
        icon: "mdi:account-voice",
        title: "Suporte à carteira ativa",
        description: "Cliente atual com dúvida específica? Alfred transfere direto pro contador responsável.",
      },
      {
        icon: "mdi:webhook",
        title: "Integração com seu sistema",
        description: "Alfred pode acionar seu sistema ou planilha para consultas e atualizações automáticas.",
      },
    ],
    steps: [
      "Cadastre as dúvidas frequentes e os responsáveis por cada tipo de cliente",
      "Cliente ou prospect manda a dúvida pelo WhatsApp",
      "Alfred responde sozinho ou encaminha pro contador certo",
    ],
    followUp: {
      title: "Prazo perto, o Alfred lembra o cliente.",
      subtitle: "Reative prospects parados e avise a carteira sobre prazos importantes.",
      items: [
        "Reative prospects que não fecharam contrato",
        "Avise sobre prazos fiscais importantes",
        "Acompanhe dúvidas recorrentes sem planilha",
      ],
    },
    ctaPhrase: "Menos ligação de dúvida, mais tempo pro fechamento.",
  },
  {
    slug: "seguros",
    route: "/seguros",
    module: "assistant",
    name: "Corretora de Seguros",
    icon: "mdi:shield-check-outline",
    tagline: "Cotação iniciada no WhatsApp, sem espera",
    seo: {
      title: "Assistente de WhatsApp com IA para Corretora de Seguros | Alfred",
      description: "O Alfred coleta os dados pra cotação, responde dúvidas de cobertura e encaminha pro corretor — direto no WhatsApp.",
    },
    hero: {
      headline: `Cotação de seguro, no WhatsApp.<br/>O <span style="color:#E4DA1B;">Alfred</span> começa por você.`,
      subheadline: "Alfred coleta as informações iniciais da cotação e passa pro corretor fechar com o cliente já qualificado.",
    },
    features: [
      {
        icon: "mdi:clipboard-check-outline",
        title: "Coleta de dados pra cotação",
        description: "Alfred pergunta o que precisa pra montar a cotação inicial, sem o cliente esperar.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Respostas sobre cobertura",
        description: "Dúvidas comuns sobre planos e coberturas respondidas na hora.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência pro corretor",
        description: "Cotação pronta? Alfred passa direto pro corretor fechar.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captura de contato do interessado",
        description: "Nome e telefone registrados mesmo se o cliente não fechar na hora.",
      },
    ],
    steps: [
      "Cadastre os tipos de seguro e as perguntas de cada cotação",
      "Cliente inicia a cotação pelo WhatsApp",
      "Alfred coleta os dados e encaminha pro corretor fechar",
    ],
    followUp: {
      title: "Cotação parada? O Alfred retoma.",
      subtitle: "Reative quem começou a cotação e não fechou, com mensagens automáticas.",
      items: [
        "Reative cotações que ficaram no meio do caminho",
        "Avise sobre renovação de apólice",
        "Acompanhe quantas cotações viraram venda",
      ],
    },
    ctaPhrase: "Cotação rápida, corretor com o cliente pronto pra fechar.",
  },
  {
    slug: "mentoria",
    route: "/mentoria",
    module: "assistant",
    name: "Consultoria/Coaching/Mentoria",
    icon: "mdi:account-tie-outline",
    tagline: "Lead qualificado, aluno bem atendido",
    seo: {
      title: "Assistente de WhatsApp com IA para Consultoria e Mentoria | Alfred",
      description: "O Alfred qualifica quem tem interesse na mentoria, tira dúvida de aluno matriculado e cobra por Pix — direto no WhatsApp.",
    },
    hero: {
      headline: `Interesse na mentoria? O <span style="color:#E4DA1B;">Alfred</span> qualifica.<br/>Aluno com dúvida? Ele também responde.`,
      subheadline: "Alfred conversa com quem quer entrar no programa e dá suporte a quem já é aluno.",
    },
    features: [
      {
        icon: "mdi:message-text-outline",
        title: "Qualificação de interessado",
        description: "Alfred entende o momento e o objetivo de quem procura a mentoria antes de encaminhar.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Suporte ao aluno matriculado",
        description: "Dúvidas sobre conteúdo, acesso e cronograma respondidas direto no chat.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Cobrança de módulo por Pix",
        description: "Fechou a mentoria? Alfred envia a chave Pix e confirma o pagamento.",
      },
      {
        icon: "mdi:account-voice",
        title: "Transferência pro mentor",
        description: "Situação que exige atenção direta? Alfred passa a conversa com o contexto.",
      },
    ],
    steps: [
      "Cadastre o programa, os módulos e a chave Pix de recebimento",
      "Interessado ou aluno manda mensagem pelo WhatsApp",
      "Alfred qualifica, cobra ou dá suporte, e escala quando precisa",
    ],
    followUp: {
      title: "Interessado não decidiu? O Alfred continua a conversa.",
      subtitle: "Reative quem demonstrou interesse e não fechou, e acompanhe o aluno matriculado.",
      items: [
        "Reative interessados que não fecharam",
        "Avise sobre novas turmas e conteúdos",
        "Acompanhe engajamento do aluno sem planilha",
      ],
    },
    ctaPhrase: "Mais alunos qualificados, mentor com mais tempo pra ensinar.",
  },
  {
    slug: "infoprodutos",
    route: "/infoprodutos",
    module: "assistant",
    name: "Infoprodutos/Cursos Online",
    icon: "mdi:play-box-outline",
    tagline: "Venda e suporte de curso, tudo no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Infoprodutos e Cursos Online | Alfred",
      description: "O Alfred vende seu curso, cobra por Pix, recupera carrinho abandonado e dá suporte ao aluno — direto no WhatsApp.",
    },
    hero: {
      headline: `Curso vendido, aluno atendido.<br/>O <span style="color:#E4DA1B;">Alfred</span> cuida dos dois.`,
      subheadline: "Alfred responde dúvida de quem quer comprar, fecha a venda por Pix e dá suporte a quem já é aluno.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas sobre o curso",
        description: "Conteúdo, formato e garantia respondidos automaticamente pra quem tá decidindo comprar.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Venda com Pix no chat",
        description: "Decidiu comprar? Alfred manda a chave Pix e confirma o pagamento na hora.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Suporte ao aluno matriculado",
        description: "Dúvida de acesso ou conteúdo? Alfred responde direto no WhatsApp.",
      },
      {
        icon: "mdi:cart-outline",
        title: "Recuperação de carrinho abandonado",
        description: "Quem começou a comprar e não terminou recebe um follow-up automático.",
      },
    ],
    steps: [
      "Cadastre as informações do curso e a chave Pix de recebimento",
      "Interessado tira dúvida ou aluno pede suporte pelo WhatsApp",
      "Alfred responde, vende e recupera quem não fechou",
    ],
    followUp: {
      title: "Carrinho abandonado não fica esquecido. O Alfred recupera.",
      subtitle: "Reative quem quase comprou e não fechou, com mensagens automáticas.",
      items: [
        "Recupere quem abandonou a compra no meio do caminho",
        "Avise sobre novas turmas e bônus",
        "Acompanhe conversão sem depender de planilha",
      ],
    },
    ctaPhrase: "Mais vendas fechadas, aluno sempre bem atendido.",
  },
  {
    slug: "ecommerce-sac",
    route: "/ecommerce-sac",
    module: "assistant",
    name: "E-commerce/Loja Virtual",
    icon: "mdi:cart-check",
    tagline: "Suporte pós-venda sem sobrecarregar o time",
    seo: {
      title: "Assistente de WhatsApp com IA para SAC de E-commerce | Alfred",
      description: "O Alfred responde dúvidas de pedido, troca e devolução, consulta seu sistema via integração e escala o que precisa de humano — direto no WhatsApp.",
    },
    hero: {
      headline: `"Cadê meu pedido?" O <span style="color:#E4DA1B;">Alfred</span> já responde.<br/>Sem sobrecarregar seu time.`,
      subheadline: "Alfred responde as dúvidas de pós-venda mais comuns e só escala pro seu time quando o caso exige.",
    },
    features: [
      {
        icon: "mdi:webhook",
        title: "Consulta ao seu sistema",
        description: "Alfred integra com sua plataforma de loja pra puxar status de pedido em tempo real.",
      },
      {
        icon: "mdi:book-open-variant",
        title: "Política de troca e devolução",
        description: "Prazos e condições respondidos sozinho, direto da sua base de conhecimento.",
      },
      {
        icon: "mdi:account-voice",
        title: "Escalonamento pro time humano",
        description: "Caso fora do padrão? Alfred transfere com o histórico completo da conversa.",
      },
      {
        icon: "mdi:clock-fast",
        title: "Atendimento fora do horário",
        description: "Cliente pergunta de madrugada, Alfred responde. Sua equipe só entra se precisar.",
      },
    ],
    steps: [
      "Cadastre as políticas da loja e conecte o webhook do seu sistema de pedidos",
      "Cliente manda a dúvida sobre o pedido pelo WhatsApp",
      "Alfred responde com o status real ou escala pro seu time",
    ],
    followUp: {
      title: "Cliente insatisfeito não desaparece. O Alfred acompanha.",
      subtitle: "Alfred retoma quem ficou sem resposta e avisa sobre atualizações do pedido.",
      items: [
        "Retome conversas que ficaram sem resposta",
        "Avise sobre atualização de status de entrega",
        "Acompanhe volume de chamados sem esforço",
      ],
    },
    ctaPhrase: "Menos chamado manual, cliente com resposta na hora.",
  },
  {
    slug: "condominio",
    route: "/condominio",
    module: "assistant",
    name: "Síndico/Administradora de Condomínio",
    icon: "mdi:office-building-marker-outline",
    tagline: "Morador atendido sem grupo de WhatsApp lotado",
    seo: {
      title: "Assistente de WhatsApp com IA para Condomínios | Alfred",
      description: "O Alfred responde dúvidas do regimento, recebe chamados de manutenção e avisa os moradores — direto no WhatsApp.",
    },
    hero: {
      headline: `Morador com dúvida ou chamado?<br/>O <span style="color:#E4DA1B;">Alfred</span> resolve no WhatsApp.`,
      subheadline: "Alfred responde o regimento, recebe chamados de manutenção e só chama o síndico quando precisa.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas do regimento",
        description: "Regras, horários de área comum e informações institucionais respondidas sozinho.",
      },
      {
        icon: "mdi:clipboard-check-outline",
        title: "Abertura de chamado",
        description: "Morador relata o problema e Alfred registra o chamado com os detalhes.",
      },
      {
        icon: "mdi:account-voice",
        title: "Escalonamento pro síndico",
        description: "Caso urgente ou fora do padrão? Alfred transfere direto.",
      },
      {
        icon: "mdi:bullhorn-outline",
        title: "Avisos para os moradores",
        description: "Dispare comunicados e avisos importantes pra todo o condomínio de uma vez.",
      },
    ],
    steps: [
      "Cadastre o regimento e o fluxo de abertura de chamado",
      "Morador manda dúvida ou chamado pelo WhatsApp",
      "Alfred responde ou registra o chamado e aciona o síndico se precisar",
    ],
    followUp: {
      title: "Chamado sem retorno incomoda. O Alfred acompanha o status.",
      subtitle: "Alfred avisa o morador sobre o andamento do chamado automaticamente.",
      items: [
        "Avise sobre o andamento de um chamado aberto",
        "Retome dúvidas que ficaram sem resposta",
        "Acompanhe chamados sem depender de planilha",
      ],
    },
    ctaPhrase: "Menos grupo de zap lotado, mais morador bem atendido.",
  },
  {
    slug: "eventos",
    route: "/eventos",
    module: "assistant",
    name: "Eventos",
    icon: "mdi:ticket-confirmation-outline",
    tagline: "Ingresso vendido, presença confirmada, tudo no WhatsApp",
    seo: {
      title: "Assistente de WhatsApp com IA para Eventos | Alfred",
      description: "O Alfred responde dúvidas sobre o evento, confirma presença e vende ingresso por Pix — direto no WhatsApp.",
    },
    hero: {
      headline: `Dúvida sobre o evento? Ingresso pra vender?<br/>O <span style="color:#E4DA1B;">Alfred</span> resolve os dois.`,
      subheadline: "Alfred responde sobre programação e local, confirma presença e vende ingresso direto no chat.",
    },
    features: [
      {
        icon: "mdi:book-open-variant",
        title: "Respostas sobre o evento",
        description: "Data, local, programação e regras respondidas automaticamente.",
      },
      {
        icon: "mdi:cash-fast",
        title: "Venda de ingresso por Pix",
        description: "Interessado decidiu ir? Alfred cobra e confirma a compra na hora.",
      },
      {
        icon: "mdi:clipboard-check-outline",
        title: "Confirmação de presença",
        description: "Alfred registra quem confirmou presença sem precisar de formulário.",
      },
      {
        icon: "mdi:account-plus-outline",
        title: "Captação pra próximos eventos",
        description: "Quem participou fica na base pra ser avisado do próximo.",
      },
    ],
    steps: [
      "Cadastre as informações do evento e a chave Pix de recebimento",
      "Interessado tira dúvida ou compra o ingresso pelo WhatsApp",
      "Alfred responde, vende e confirma presença sozinho",
    ],
    followUp: {
      title: "Quem não confirmou, o Alfred lembra antes do evento.",
      subtitle: "Reative interessados que não compraram e avise sobre próximos eventos.",
      items: [
        "Lembrete antes da data do evento",
        "Reative interessados que não fecharam a compra",
        "Avise a base sobre o próximo evento",
      ],
    },
    ctaPhrase: "Mais ingressos vendidos, evento lotado.",
  },
];
